/**
 * Billing Context
 * 
 * Manages all billing/claims-related state and API calls.
 * This context is ONLY wrapped around the Billing tab for crash isolation.
 * Data is fetched on-demand when the tab is opened.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BillingContext = createContext();

export const BillingProvider = ({ children }) => {
  // Claims queue data
  const [claimsQueue, setClaimsQueue] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  
  // Notes ready for billing
  const [notesReadyForBilling, setNotesReadyForBilling] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Selected items
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimDetail, setClaimDetail] = useState(null);
  const [loadingClaimDetail, setLoadingClaimDetail] = useState(false);
  
  // UI state
  const [showClaimDetail, setShowClaimDetail] = useState(false);
  const [showClaimDetailDialog, setShowClaimDetailDialog] = useState(false);
  const [showReturnClaimDialog, setShowReturnClaimDialog] = useState(false);
  const [returnClaimNotes, setReturnClaimNotes] = useState('');
  const [claimContextMenu, setClaimContextMenu] = useState({ show: false, x: 0, y: 0, claim: null });
  const [viewingBatchClaims, setViewingBatchClaims] = useState([]);
  const [expandedClaimVersions, setExpandedClaimVersions] = useState(new Set());
  
  // Sorting
  const [claimsSortColumn, setClaimsSortColumn] = useState('date_of_service');
  const [claimsSortDirection, setClaimsSortDirection] = useState('desc');
  
  // Filters (persisted to localStorage)
  const [billingProviderFilter, setBillingProviderFilter] = useState(() => {
    try {
      const stored = localStorage.getItem('billingProviderFilter');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* migration from old string format — ignore */ }
    return [];
  });
  // 'include' (default, selected providers are shown) or 'exclude' (selected providers are hidden)
  const [billingProviderFilterMode, setBillingProviderFilterModeState] = useState(() => {
    const stored = localStorage.getItem('billingProviderFilterMode');
    return stored === 'exclude' ? 'exclude' : 'include';
  });
  const setBillingProviderFilterMode = useCallback((mode) => {
    const next = mode === 'exclude' ? 'exclude' : 'include';
    setBillingProviderFilterModeState(next);
    localStorage.setItem('billingProviderFilterMode', next);
  }, []);
  const [billingStatusFilter, setBillingStatusFilter] = useState('ready_for_billing');
  const [billingSearchTerm, setBillingSearchTerm] = useState('');
  const [billingClientFilter, setBillingClientFilter] = useState(() => {
    // Array of client IDs. Migrates from legacy string format ('all' or single id).
    try {
      const stored = localStorage.getItem('billingClientFilter');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* legacy string — fall through */ }
    const legacy = localStorage.getItem('billingClientFilter');
    if (legacy && legacy !== 'all') return [legacy];
    return [];
  });
  const [billingClientFilterMode, setBillingClientFilterModeState] = useState(() => {
    return localStorage.getItem('billingClientFilterMode') === 'exclude' ? 'exclude' : 'include';
  });
  const setBillingClientFilterMode = useCallback((mode) => {
    const next = mode === 'exclude' ? 'exclude' : 'include';
    setBillingClientFilterModeState(next);
    localStorage.setItem('billingClientFilterMode', next);
  }, []);
  const [billingInsuranceFilter, setBillingInsuranceFilter] = useState(() => {
    // Array of insurance NAMES (matches the existing filter logic which
    // matches by name) plus the special token 'Self Pay'.  Migrates from
    // the legacy single-string format ('all' or single name).
    try {
      const stored = localStorage.getItem('billingInsuranceFilter');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* legacy string — fall through */ }
    const legacy = localStorage.getItem('billingInsuranceFilter');
    if (legacy && legacy !== 'all') return [legacy];
    return [];
  });
  const [billingInsuranceFilterMode, setBillingInsuranceFilterModeState] = useState(() => {
    return localStorage.getItem('billingInsuranceFilterMode') === 'exclude' ? 'exclude' : 'include';
  });
  const setBillingInsuranceFilterMode = useCallback((mode) => {
    const next = mode === 'exclude' ? 'exclude' : 'include';
    setBillingInsuranceFilterModeState(next);
    localStorage.setItem('billingInsuranceFilterMode', next);
  }, []);
  // CPT filter — multi-select array of CPT codes (e.g. ['90834', '90837']).
  // Mirrors the insurance filter wiring: array of strings, persisted to
  // localStorage, with an include/exclude toggle.  Matching is exact
  // string equality against the note's `_billing.cpt_code` (notes) or
  // each `service_lines[].cpt_code` (claims).  Feb 2026.
  const [billingCptFilter, setBillingCptFilter] = useState(() => {
    try {
      const stored = localStorage.getItem('billingCptFilter');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) { /* legacy — fall through */ }
    return [];
  });
  const [billingCptFilterMode, setBillingCptFilterModeState] = useState(() => {
    return localStorage.getItem('billingCptFilterMode') === 'exclude' ? 'exclude' : 'include';
  });
  const setBillingCptFilterMode = useCallback((mode) => {
    const next = mode === 'exclude' ? 'exclude' : 'include';
    setBillingCptFilterModeState(next);
    localStorage.setItem('billingCptFilterMode', next);
  }, []);
  const [billingDateFrom, setBillingDateFrom] = useState(() => {
    return localStorage.getItem('billingDateFrom') || '';
  });
  const [billingDateTo, setBillingDateTo] = useState(() => {
    return localStorage.getItem('billingDateTo') || '';
  });
  const [claimSearchTerm, setClaimSearchTerm] = useState('');
  const [claimFilterProvider, setClaimFilterProvider] = useState('');
  const [claimFilterStatus, setClaimFilterStatus] = useState('');
  
  // Bulk selection
  const [selectedClaimsForPayment, setSelectedClaimsForPayment] = useState([]);
  const [selectedClaimsForInvoice, setSelectedClaimsForInvoice] = useState([]);
  const [selectedClaimsForReprocess, setSelectedClaimsForReprocess] = useState([]);
  
  // Note review
  const [selectedNoteForReview, setSelectedNoteForReview] = useState(null);
  const [noteReviewData, setNoteReviewData] = useState(null);
  const [showNoteReviewDialog, setShowNoteReviewDialog] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [notesSentForCorrection, setNotesSentForCorrection] = useState([]);
  const [showSendBackDialog, setShowSendBackDialog] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  
  // Workshop claims
  const [workshopClaims, setWorkshopClaims] = useState([]);
  const [loadingWorkshopClaims, setLoadingWorkshopClaims] = useState(false);
  
  // Workshop summary
  const [workshopSummary, setWorkshopSummary] = useState({
    total_claims: 0,
    denied_claims: 0,
    outstanding_balance: 0,
    aging_90_plus: 0
  });
  const [loadingWorkshopSummary, setLoadingWorkshopSummary] = useState(false);
  
  // Billing settings
  const [billingSettings, setBillingSettings] = useState({
    billing_enabled: true
  });
  const [billingSettingsLoading, setBillingSettingsLoading] = useState(false);
  
  // Clearinghouse/Availity status
  const [clearinghouseStatus, setClearinghouseStatus] = useState(null);
  const [loadingClearinghouseStatus, setLoadingClearinghouseStatus] = useState(false);

  // Listen for optimistic note status changes from App.js
  useEffect(() => {
    const handler = (e) => {
      const { noteId, newStatus, noteData } = e.detail || {};
      if (!noteId) return;
      if (newStatus === 'signed_ready_for_billing') {
        // Note moved TO billing — will be picked up on next fetch
      } else if (newStatus === 'needs_corrections') {
        setNotesReadyForBilling(prev => prev.filter(n => n.id !== noteId));
        // Add to corrections with whatever data we have
        if (noteData) {
          setNotesSentForCorrection(prev => {
            if (prev.some(n => n.id === noteId)) return prev;
            return [...prev, { ...noteData, status: 'needs_corrections' }];
          });
        }
      } else {
        // Any other status (billed, paid, locked, draft, etc) — remove from billing lists
        setNotesReadyForBilling(prev => prev.filter(n => n.id !== noteId));
        setNotesSentForCorrection(prev => prev.filter(n => n.id !== noteId));
      }
    };
    window.addEventListener('noteStatusChanged', handler);
    return () => window.removeEventListener('noteStatusChanged', handler);
  }, []);

  /**
   * Fetch claims queue on-demand (only when Billing tab is opened)
   */
  const fetchClaimsQueue = useCallback(async () => {
    if (loadingClaims) return;
    
    setLoadingClaims(true);
    try {
      void 0 && console.log('[Billing] Fetching claims queue on-demand...');
      
      const params = new URLSearchParams();
      if (Array.isArray(billingProviderFilter) && billingProviderFilter.length === 1) {
        params.append('provider_id', billingProviderFilter[0]);
      }
      if (billingStatusFilter && billingStatusFilter !== 'all') {
        params.append('status', billingStatusFilter);
      }
      // Only forward client_id to the claims API for a single-client Include filter.
      // For multi-select or Exclude mode, keep it client-side-filtered to avoid
      // combinatorial API calls.
      if (Array.isArray(billingClientFilter)
          && billingClientFilter.length === 1
          && billingClientFilterMode !== 'exclude') {
        params.append('client_id', billingClientFilter[0]);
      }
      if (billingInsuranceFilter
          && Array.isArray(billingInsuranceFilter)
          && billingInsuranceFilter.length === 1
          && billingInsuranceFilterMode !== 'exclude') {
        // Backend supports a single ``insurance_company`` name filter —
        // for multi-select or Exclude mode we keep filtering client-side.
        params.append('insurance_company', billingInsuranceFilter[0]);
      } else if (typeof billingInsuranceFilter === 'string'
                 && billingInsuranceFilter
                 && billingInsuranceFilter !== 'all') {
        // Legacy callers that haven't migrated to the array shape.
        params.append('insurance_company', billingInsuranceFilter);
      }
      if (billingDateFrom) {
        params.append('date_from', billingDateFrom);
      }
      if (billingDateTo) {
        params.append('date_to', billingDateTo);
      }
      
      const response = await axios.get(`${API}/api/claims/queue?${params.toString()}`);
      const data = response.data || {};
      setClaimsQueue(data.claims || data || []);
      setClaimsLoaded(true);
      
      void 0 && console.log('[Billing] Claims loaded:', response.data?.length || 0);
    } catch (error) {
      console.error('[Billing] Failed to fetch claims:', error);
      setClaimsQueue([]);
    } finally {
      setLoadingClaims(false);
    }
  }, [loadingClaims, billingProviderFilter, billingStatusFilter, billingClientFilter, billingInsuranceFilter, billingInsuranceFilterMode, billingDateFrom, billingDateTo]);

  /**
   * Fetch notes ready for billing
   */
  const fetchNotesForBilling = useCallback(async () => {
    if (loadingNotes) return;
    
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API}/api/billing/notes-ready`);
      void 0 && console.log('[Billing] Fetched notes ready for billing:', response.data?.length || 0);
      setNotesReadyForBilling(response.data || []);
    } catch (error) {
      console.error('[Billing] Failed to fetch notes for billing:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, [loadingNotes]);

  /**
   * Fetch notes sent back for corrections
   *
   * Feb 2026 — added ``summary=true`` so the list view receives a
   * 4 KB projection instead of the 68 KB full payload (content blobs
   * are not needed here).  Same change was already applied to
   * App.js's mirror copy of this fetcher.
   */
  const fetchNotesSentForCorrection = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/notes?status=needs_corrections&limit=1000&start_date=2020-01-01&summary=true`);
      const notes = response.data || [];
      
      // Batch-fetch client info for enrichment
      const clientIds = [...new Set(notes.map(n => n.client_id).filter(Boolean))];
      const clientMap = {};
      if (clientIds.length > 0) {
        // Feb 2026 perf fix: was N+1 — one GET /clients/{id} per row,
        // serialized server-side by the Mongo pool.  Now a single
        // bulk fetch via the new ids= query param.
        const chunks = [];
        for (let i = 0; i < clientIds.length; i += 200) {
          chunks.push(clientIds.slice(i, i + 200));
        }
        const responses = await Promise.all(
          chunks.map(chunk =>
            axios
              .get(`${API}/api/clients?summary=true&ids=${chunk.join(',')}&page_size=200&include_archived=true`)
              .then(r => r.data?.clients || [])
              .catch(() => [])
          )
        );
        responses.flat().forEach(c => { if (c) clientMap[c.id] = c; });
      }
      
      // Enrich notes
      notes.forEach(note => {
        const client = clientMap[note.client_id];
        if (client) {
          note._client_name = client.full_name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unknown';
          note._client_mrn = client.mrn || '';
          const ins = (client.insurances || []).find(i => i.priority === 1 && i.status === 'active') || (client.insurances || []).find(i => i.status === 'active');
          note._client_insurance = ins?.company || client.primary_insurance || 'Self Pay';
          note._client_is_self_pay = !ins?.company;
        }
      });
      
      void 0 && console.log('[Billing] Fetched notes sent for correction:', notes.length);
      setNotesSentForCorrection(notes);
    } catch (error) {
      console.error('[Billing] Failed to fetch notes sent for correction:', error);
    }
  }, []);

  /**
   * Fetch claim detail
   */
  const fetchClaimDetail = useCallback(async (claimId) => {
    setLoadingClaimDetail(true);
    try {
      const response = await axios.get(`${API}/api/claims/${claimId}`);
      setClaimDetail(response.data);
      return response.data;
    } catch (error) {
      console.error('[Billing] Failed to fetch claim detail:', error);
      throw error;
    } finally {
      setLoadingClaimDetail(false);
    }
  }, []);

  /**
   * Fetch billing settings
   */
  const fetchBillingSettings = useCallback(async () => {
    if (billingSettingsLoading) return;
    
    setBillingSettingsLoading(true);
    try {
      const response = await axios.get(`${API}/api/billing/settings`);
      setBillingSettings(response.data || { billing_enabled: true });
      void 0 && console.log('[Billing] Settings loaded:', response.data);
    } catch (error) {
      console.error('[Billing] Failed to fetch billing settings:', error);
      setBillingSettings({ billing_enabled: true });
    } finally {
      setBillingSettingsLoading(false);
    }
  }, [billingSettingsLoading]);

  /**
   * Fetch clearinghouse status (Availity/Optum)
   * Uses the /api/billing/ prefix for specialized billing endpoints
   */
  const fetchClearinghouseStatus = useCallback(async () => {
    if (loadingClearinghouseStatus) return;
    
    setLoadingClearinghouseStatus(true);
    try {
      const response = await axios.get(`${API}/api/billing/clearinghouse/status`);
      setClearinghouseStatus(response.data);
      void 0 && console.log('[Billing] Clearinghouse status loaded:', response.data?.status);
    } catch (error) {
      console.error('[Billing] Failed to fetch clearinghouse status:', error);
      setClearinghouseStatus(null);
    } finally {
      setLoadingClearinghouseStatus(false);
    }
  }, [loadingClearinghouseStatus]);

  /**
   * Download 271 responses from Availity
   * Uses the /api/billing/ prefix for Availity endpoints
   */
  const downloadAvailityResponses = useCallback(async (clientId = null) => {
    try {
      const url = clientId 
        ? `${API}/api/billing/availity/download-271-responses?client_id=${clientId}`
        : `${API}/api/billing/availity/download-271-responses`;
      
      const response = await axios.post(url);
      void 0 && console.log('[Billing] Availity 271 download:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Billing] Failed to download Availity responses:', error);
      throw error;
    }
  }, []);

  /**
   * Fetch workshop claims
   */
  const fetchWorkshopClaims = useCallback(async () => {
    if (loadingWorkshopClaims) return;
    
    setLoadingWorkshopClaims(true);
    try {
      const response = await axios.get(`${API}/api/claims/workshop`);
      setWorkshopClaims(response.data?.claims || []);
      void 0 && console.log('[Billing] Workshop claims loaded:', response.data?.claims?.length || 0);
    } catch (error) {
      console.error('[Billing] Failed to fetch workshop claims:', error);
      setWorkshopClaims([]);
    } finally {
      setLoadingWorkshopClaims(false);
    }
  }, [loadingWorkshopClaims]);

  /**
   * Fetch workshop summary statistics
   */
  const fetchWorkshopSummary = useCallback(async () => {
    if (loadingWorkshopSummary) return;
    
    setLoadingWorkshopSummary(true);
    try {
      const response = await axios.get(`${API}/api/claims/workshop/summary`);
      const data = response.data || {};
      setWorkshopSummary({
        total_claims: data.by_status ? Object.values(data.by_status).reduce((sum, s) => sum + s.count, 0) : 0,
        denied_claims: data.by_status?.denied?.count || 0,
        outstanding_balance: data.by_status ? Object.values(data.by_status).reduce((sum, s) => sum + (s.charges || 0), 0) : 0,
        aging_90_plus: data.aging?.['90+_days'] || 0
      });
      void 0 && console.log('[Billing] Workshop summary loaded');
    } catch (error) {
      console.error('[Billing] Failed to fetch workshop summary:', error);
    } finally {
      setLoadingWorkshopSummary(false);
    }
  }, [loadingWorkshopSummary]);

  /**
   * Save filter to localStorage
   */
  const saveFilter = useCallback((key, value) => {
    localStorage.setItem(key, value);
  }, []);

  /**
   * Update billing provider filter (with persistence) — value is an array of provider IDs
   */
  const updateBillingProviderFilter = useCallback((value) => {
    const arr = Array.isArray(value) ? value : [];
    setBillingProviderFilter(arr);
    localStorage.setItem('billingProviderFilter', JSON.stringify(arr));
  }, []);

  /**
   * Update billing client filter (with persistence)
   */
  const updateBillingClientFilter = useCallback((value) => {
    // Always normalize to an array.
    const arr = Array.isArray(value)
      ? value
      : (!value || value === 'all' ? [] : [value]);
    setBillingClientFilter(arr);
    localStorage.setItem('billingClientFilter', JSON.stringify(arr));
  }, []);

  /**
   * Update billing insurance filter (with persistence).  Always
   * normalized to an array of insurance names.
   */
  const updateBillingInsuranceFilter = useCallback((value) => {
    const arr = Array.isArray(value)
      ? value
      : (!value || value === 'all' ? [] : [value]);
    setBillingInsuranceFilter(arr);
    localStorage.setItem('billingInsuranceFilter', JSON.stringify(arr));
  }, []);

  /**
   * Update billing CPT filter (with persistence).  Always normalized
   * to an array of CPT code strings.  Empty array = no filter.
   */
  const updateBillingCptFilter = useCallback((value) => {
    const arr = Array.isArray(value)
      ? value
      : (!value || value === 'all' ? [] : [value]);
    setBillingCptFilter(arr);
    localStorage.setItem('billingCptFilter', JSON.stringify(arr));
  }, []);

  /**
   * Update billing date from (with persistence)
   */
  const updateBillingDateFrom = useCallback((value) => {
    setBillingDateFrom(value);
    saveFilter('billingDateFrom', value);
  }, [saveFilter]);

  /**
   * Update billing date to (with persistence)
   */
  const updateBillingDateTo = useCallback((value) => {
    setBillingDateTo(value);
    saveFilter('billingDateTo', value);
  }, [saveFilter]);

  // Phase-2 stabilization (Feb 2026): memoize the Provider value.
  // This was the SINGLE LARGEST remaining drain — 51 useStates, 21
  // useCallbacks, and consumed by every Billing-tab sub-surface
  // (Claims, Workshop, Batches, ERAs, Denials, AR Aging, Payment
  // Receipts).  All 21 helper functions are already useCallback-
  // stable; only the Provider ``value`` literal was missing.
  //
  // Setters from useState are stable — intentionally NOT in deps.
  // The custom mode-setters (``setBillingProviderFilterMode``,
  // ``setBillingClientFilterMode`` etc) are useCallback'd above with
  // empty deps so they are also stable.  The action functions
  // (``fetchClaimsQueue`` etc) have their own deps internally so
  // their identity does change when those deps change — that's
  // correct; consumers that destructure them need to re-render to
  // pick up the new closure.
  const value = useMemo(() => ({
    // Claims data
    claimsQueue,
    setClaimsQueue,
    loadingClaims,
    claimsLoaded,
    
    // Notes for billing
    notesReadyForBilling,
    setNotesReadyForBilling,
    loadingNotes,
    
    // Selected items
    selectedClaim,
    setSelectedClaim,
    claimDetail,
    setClaimDetail,
    loadingClaimDetail,
    
    // UI state
    showClaimDetail,
    setShowClaimDetail,
    showClaimDetailDialog,
    setShowClaimDetailDialog,
    showReturnClaimDialog,
    setShowReturnClaimDialog,
    returnClaimNotes,
    setReturnClaimNotes,
    claimContextMenu,
    setClaimContextMenu,
    viewingBatchClaims,
    setViewingBatchClaims,
    expandedClaimVersions,
    setExpandedClaimVersions,
    
    // Sorting
    claimsSortColumn,
    setClaimsSortColumn,
    claimsSortDirection,
    setClaimsSortDirection,
    
    // Filters
    billingProviderFilter,
    setBillingProviderFilter: updateBillingProviderFilter,
    billingProviderFilterMode,
    setBillingProviderFilterMode,
    billingStatusFilter,
    setBillingStatusFilter,
    billingSearchTerm,
    setBillingSearchTerm,
    billingClientFilter,
    setBillingClientFilter: updateBillingClientFilter,
    billingClientFilterMode,
    setBillingClientFilterMode,
    billingInsuranceFilter,
    setBillingInsuranceFilter: updateBillingInsuranceFilter,
    billingInsuranceFilterMode,
    setBillingInsuranceFilterMode,
    billingCptFilter,
    setBillingCptFilter: updateBillingCptFilter,
    billingCptFilterMode,
    setBillingCptFilterMode,
    billingDateFrom,
    setBillingDateFrom: updateBillingDateFrom,
    billingDateTo,
    setBillingDateTo: updateBillingDateTo,
    claimSearchTerm,
    setClaimSearchTerm,
    claimFilterProvider,
    setClaimFilterProvider,
    claimFilterStatus,
    setClaimFilterStatus,
    
    // Bulk selection
    selectedClaimsForPayment,
    setSelectedClaimsForPayment,
    selectedClaimsForInvoice,
    setSelectedClaimsForInvoice,
    selectedClaimsForReprocess,
    setSelectedClaimsForReprocess,
    
    // Note review
    selectedNoteForReview,
    setSelectedNoteForReview,
    noteReviewData,
    setNoteReviewData,
    showNoteReviewDialog,
    setShowNoteReviewDialog,
    currentNoteIndex,
    setCurrentNoteIndex,
    notesSentForCorrection,
    setNotesSentForCorrection,
    fetchNotesSentForCorrection,
    showSendBackDialog,
    setShowSendBackDialog,
    correctionReason,
    setCorrectionReason,
    
    // Workshop
    workshopClaims,
    setWorkshopClaims,
    loadingWorkshopClaims,
    setLoadingWorkshopClaims,
    workshopSummary,
    setWorkshopSummary,
    loadingWorkshopSummary,
    
    // Billing settings
    billingSettings,
    setBillingSettings,
    billingSettingsLoading,
    
    // Clearinghouse/Availity
    clearinghouseStatus,
    setClearinghouseStatus,
    loadingClearinghouseStatus,
    
    // Actions
    fetchClaimsQueue,
    fetchNotesForBilling,
    fetchClaimDetail,
    fetchBillingSettings,
    fetchClearinghouseStatus,
    downloadAvailityResponses,
    fetchWorkshopClaims,
    fetchWorkshopSummary,
  }), [
    // Claims data
    claimsQueue, loadingClaims, claimsLoaded,
    // Notes
    notesReadyForBilling, loadingNotes,
    // Selected
    selectedClaim, claimDetail, loadingClaimDetail,
    // UI
    showClaimDetail, showClaimDetailDialog, showReturnClaimDialog, returnClaimNotes,
    claimContextMenu, viewingBatchClaims, expandedClaimVersions,
    // Sorting
    claimsSortColumn, claimsSortDirection,
    // Filters
    billingProviderFilter, billingProviderFilterMode,
    billingStatusFilter, billingSearchTerm,
    billingClientFilter, billingClientFilterMode,
    billingInsuranceFilter, billingInsuranceFilterMode,
    billingCptFilter, billingCptFilterMode,
    billingDateFrom, billingDateTo,
    claimSearchTerm, claimFilterProvider, claimFilterStatus,
    // Bulk
    selectedClaimsForPayment, selectedClaimsForInvoice, selectedClaimsForReprocess,
    // Review
    selectedNoteForReview, noteReviewData, showNoteReviewDialog, currentNoteIndex,
    notesSentForCorrection, showSendBackDialog, correctionReason,
    // Workshop
    workshopClaims, loadingWorkshopClaims, workshopSummary, loadingWorkshopSummary,
    // Settings
    billingSettings, billingSettingsLoading,
    // Clearinghouse
    clearinghouseStatus, loadingClearinghouseStatus,
    // Stable mode setters (useCallback []) — included for correctness
    setBillingProviderFilterMode, setBillingClientFilterMode,
    setBillingInsuranceFilterMode, setBillingCptFilterMode,
    // Persisted-filter wrappers (useCallback []) — stable
    updateBillingProviderFilter, updateBillingClientFilter,
    updateBillingInsuranceFilter, updateBillingCptFilter,
    updateBillingDateFrom, updateBillingDateTo,
    // Action functions — their identity DOES change when their own
    // internal deps change.  Listing them here is correct (and
    // matches the eslint exhaustive-deps rule).
    fetchClaimsQueue, fetchNotesForBilling, fetchClaimDetail,
    fetchBillingSettings, fetchClearinghouseStatus,
    downloadAvailityResponses,
    fetchWorkshopClaims, fetchWorkshopSummary,
    fetchNotesSentForCorrection,
  ]);

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within BillingProvider');
  }
  return context;
};

export default BillingContext;
